import { useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";

import { POLLING_STATUS_CODES } from "@/shared/gift-cards.constants";

import { processOrder } from "@/utils/afterpay-checkout";
import { generatePriceFromCurrencyCode } from "@/utils/strings";
import { fadeIn, fadeOut } from "@/utils/transitions";

import { localeCurrencyCodeMap } from "@/translations/config";

import { UI_CONTEXTS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";
import { trackPageLoad, trackUserEvent } from "@/analytics/tracking";

import { useErrorMessage } from "@/context/ErrorMessageContext";
import { useCurrentLocale } from "@/context/LanguageContext";

import { BreadCrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { PAGE_TYPES } from "@/components/breadcrumbs/breadcrumbs.constants";
import { DropdownMenuItem } from "@/components/dropdown-menus/dropdown-menu-items/dropdown-menu-item-interface";
import { LoaderOverlay } from "@/components/loaders/loader-overlay";
import SendToFriendModal from "@/components/modals/forms/buy-for-friend/send-to-friend-modal";
import BuyForYourselfModal from "@/components/modals/forms/buy-for-yourself/buy-for-yourself-modal";

import { useIsArcade } from "@/hooks/useIsArcade";

import {
    GiftCardProps,
    Consumer,
    Recipient,
    GiftCardCheckoutData,
} from "@/interfaces/gift-cards/gift-card-interface";
import { transformCheckout } from "@/interfaces/gift-cards/gift-card-transform";

import GiftCardImageGridHeader from "../gift-card-image-grid-header/gift-card-image-grid-header";
import GiftCardDetails from "./gift-card-details";
import styles from "./gift-card-details.module.scss";
import { GiftCardHeroBanner } from "./gift-card-hero-banner/gift-card-hero-banner";
import GiftCardImage from "./gift-card-image";
import { STOCK_LEVEL } from "./gift-cards.constants";
import OrderConfirmation from "./order-confirmation";

interface Props {
    giftCard: GiftCardProps;
    consumer: Consumer;
    recipient: Recipient;
    pollingStatus: string;
    isPurchaseFlowComplete: boolean;
    setConsumer: (consumer: Consumer) => void;
    showHeroOnlyVariant?: boolean;
    setRecipient: (recipient: Recipient) => void;
    setOrderId: (orderId: number) => void;
    setPurchaseFlowComplete: (isComplete: boolean) => void;
    setShowErrorOverlay: (showErrorOverlay: boolean) => void;
}

const GiftCardDetailsLayout: React.FC<Props> = ({
    giftCard,
    consumer,
    recipient,
    pollingStatus,
    isPurchaseFlowComplete,
    setConsumer,
    setRecipient,
    setOrderId,
    setPurchaseFlowComplete,
    setShowErrorOverlay,
    showHeroOnlyVariant = false,
}) => {
    const { denominations, id, imageUrl, name, links } = giftCard;
    const [menuItems, setMenuItem] = useState<Array<DropdownMenuItem>>([]);
    const [selectedAmount, setSelectedAmount] = useState<string>("");
    const [confirmOrder, setConfirmOrder] = useState(false);
    const [isGift, setIsGift] = useState<boolean>(false);
    const [isSendToFriendModalVisible, setSendToFriendModalVisible] = useState(false);
    const [isSendToFriendModalFading, setSendToFriendModalFading] = useState(false);
    const [isBuyForYourselfModalVisible, setBuyForYourselfModalVisible] = useState(false);
    const [isBuyForYourselfModalFading, setBuyForYourselfModalFading] = useState(false);
    const locale = useCurrentLocale();
    const router = useRouter();
    const isArcade = useIsArcade();
    const shouldSkipConfirm = router.query.skip_confirm === "true";
    const isGiftPurchase = router.query.is_gift === "true";
    const shouldSkipLogin = router.query.skip_login === "true";

    const eventProps = {
        newFlow: false,
    };

    const setFadeOutBuyForYourself = () =>
        fadeOut(setBuyForYourselfModalVisible, setBuyForYourselfModalFading);
    const setFadeOutBuyForAFriend = () =>
        fadeOut(setSendToFriendModalVisible, setSendToFriendModalFading);
    const onBuyForYourself = () => {
        trackUserEvent(
            locale,
            router.asPath,
            UI_CONTEXTS.GIFTCARD_RETAILER_PROFILE,
            EVENTS.GIFT_CARD_BUY_FOR_YOURSELF_CLICK,
            eventProps
        );
        setIsGift(false);
        trackPageLoad(router.asPath, locale, EVENTS.GIFT_CARD_CHECKOUT_START, {
            sku: giftCard.sku,
            type: "self",
        });
        fadeIn(setBuyForYourselfModalVisible, setBuyForYourselfModalFading);
    };

    const onBuyForAFriend = () => {
        setIsGift(true);
        trackPageLoad(router.asPath, locale, EVENTS.GIFT_CARD_CHECKOUT_START, {
            sku: giftCard.sku,
            type: "other",
        });
        trackUserEvent(
            locale,
            router.asPath,
            UI_CONTEXTS.GIFTCARD_RETAILER_PROFILE,
            EVENTS.GIFT_CARD_SEND_AS_GIFT_CLICK,
            eventProps
        );
        fadeIn(setSendToFriendModalVisible, setSendToFriendModalFading);
    };

    const setFormData = useCallback(() => {
        const recipientFromMobile = window.apAppGiftCardRecipient;
        setIsGift(isGiftPurchase);
        setConsumer({
            firstname: window.apAppFirstName,
            lastname: window.apAppLastName,
            email: window.apAppEmail,
            confirmEmail: window.apAppEmail,
        });
        if (isGiftPurchase) {
            setRecipient({
                name: recipientFromMobile.name,
                email: recipientFromMobile.email,
                confirmEmail: recipientFromMobile.confirmEmail,
                message: recipientFromMobile.message,
            });
        }
        setSelectedAmount(window.apAppGiftCardAmount);
        setConfirmOrder(true);
    }, [setConsumer, setRecipient, isGiftPurchase]);

    const startSkipLogin = useCallback(() => {
        const recipientFromMobile = window.apAppGiftCardRecipient;
        setIsGift(isGiftPurchase);
        const checkoutData: GiftCardCheckoutData = {
            gift_card_id: Number(giftCard.id),
            amount: Number(window.apAppGiftCardAmount),
            currency: localeCurrencyCodeMap[locale],
            sku: giftCard.sku,
            isGift: isGiftPurchase,
            consumer: {
                firstname: window.apAppFirstName,
                lastname: window.apAppLastName,
                email: window.apAppEmail,
                confirmEmail: window.apAppEmail,
            },
            recipient: isGiftPurchase
                ? {
                      name: recipientFromMobile.name,
                      email: recipientFromMobile.email,
                      confirmEmail: recipientFromMobile.confirmEmail,
                      message: recipientFromMobile.message,
                  }
                : {
                      name: "",
                      email: "",
                      confirmEmail: "",
                      message: "",
                  },
        };
        setConsumer(checkoutData.consumer);
        setRecipient(checkoutData.recipient);
        setConfirmOrder(true);
        processOrder(
            window.apAppParamToken,
            transformCheckout(checkoutData),
            locale,
        )
            .then((orderId) => setOrderId(orderId))
            .catch(() => setShowErrorOverlay(true));
    }, [
        giftCard,
        isGiftPurchase,
        locale,
        setConsumer,
        setOrderId,
        setRecipient,
        setShowErrorOverlay,
    ]);

    useEffect(() => {
        if (shouldSkipConfirm === true) {
            setFormData();
            return;
        }

        if (shouldSkipLogin) {
            startSkipLogin();
            return;
        }

        const inStockDenominations = denominations.reduce((accum, denomination, index) => {
            if (denomination.stockLevel === STOCK_LEVEL.GOOD) {
                accum.push({
                    id: index + 1,
                    name: generatePriceFromCurrencyCode(denomination.amount, denomination.currency),
                });
            }
            return accum;
        }, [] as Array<DropdownMenuItem>);

        setMenuItem(inStockDenominations);
        setSelectedAmount(inStockDenominations[0]?.name);
    }, [denominations, shouldSkipConfirm, setFormData, shouldSkipLogin, startSkipLogin]);

    const BreadCrumb = {
        currentLink: {
            rootPageUrl: links.self,
            description: null,
            id: String(id),
            name: name,
            parentId: null,
        },
        parentLink: null,
    };
    const { errorMessages } = useErrorMessage();
    const numberOfErrorMessages = Object.values(errorMessages).length;
    const commonOrderConfirmationProps = {
        giftCard,
        isGift,
        selectedAmount,
        consumer,
        recipient,
        skipConfirmed: shouldSkipConfirm,
        skipLogin: shouldSkipLogin,
        isPurchaseFlowComplete,
        confirmOrder,
        setPurchaseFlowComplete,
        setConfirmOrder,
        setOrderId,
    };

    if (shouldSkipLogin) {
        return <OrderConfirmation {...commonOrderConfirmationProps} />;
    }

    const isNewCheckoutFlow = shouldSkipLogin || shouldSkipConfirm;
    const shouldShowLoaderOverlay =
        !isNewCheckoutFlow && !!pollingStatus && POLLING_STATUS_CODES.includes(pollingStatus);

    if (showHeroOnlyVariant) {
        return (
            <>
                <GiftCardImageGridHeader />
                <GiftCardHeroBanner locale={locale} />

                <div className={styles.giftCardLayoutContainer}>
                    {!!pollingStatus &&
                        scroll({
                            top: 0,
                            left: 0,
                            behavior: "smooth",
                        })}
                    {shouldShowLoaderOverlay && <LoaderOverlay variant="default" />}
                    <div className={styles.contentContainer}>
                        <div className={styles.controlsContainer}>
                            {numberOfErrorMessages > 0 &&
                                scroll({
                                    top: 0,
                                    left: 0,
                                    behavior: "smooth",
                                })}
                            {confirmOrder && (
                                <OrderConfirmation {...commonOrderConfirmationProps} />
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className={styles.giftCardLayoutContainer}>
            {!!pollingStatus &&
                scroll({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                })}
            {shouldShowLoaderOverlay && <LoaderOverlay variant="default" />}
            <BreadCrumbs {...BreadCrumb} pageType={PAGE_TYPES.GIFT_CARD} />
            <div
                className={styles.headerContainer}
                dangerouslySetInnerHTML={{
                    __html: isArcade ? `<h5>${name}</h5>` : `<h2>${name}</h2>`,
                }}
            />
            <div className={styles.contentContainer}>
                <div className={styles.imageContainer}>
                    <GiftCardImage imageUrl={imageUrl} />
                </div>
                <div className={styles.controlsContainer}>
                    {numberOfErrorMessages > 0 &&
                        scroll({
                            top: 0,
                            left: 0,
                            behavior: "smooth",
                        })}
                    {confirmOrder ? (
                        <OrderConfirmation {...commonOrderConfirmationProps} />
                    ) : (
                        <GiftCardDetails
                            giftCard={giftCard}
                            locale={locale}
                            menuItems={menuItems}
                            selectedAmount={selectedAmount}
                            setSelectedAmount={setSelectedAmount}
                            onBuyForYourself={onBuyForYourself}
                            onBuyForAFriend={onBuyForAFriend}
                        />
                    )}
                </div>
            </div>

            {isBuyForYourselfModalVisible && (
                <BuyForYourselfModal
                    setFadeOut={setFadeOutBuyForYourself}
                    active={isBuyForYourselfModalFading}
                    consumer={consumer}
                    setConsumer={setConsumer}
                    confirmOrder={setConfirmOrder}
                />
            )}
            {isSendToFriendModalVisible && (
                <SendToFriendModal
                    setFadeOut={setFadeOutBuyForAFriend}
                    active={isSendToFriendModalFading}
                    consumer={consumer}
                    recipient={recipient}
                    setConfirmOrder={setConfirmOrder}
                    setConsumer={setConsumer}
                    setRecipient={setRecipient}
                />
            )}
        </div>
    );
};

export default GiftCardDetailsLayout;
